/**
 * Echo Hunt -- live (DB-backed) wiring for a playable round (P9, §10 + §16.2 P9).
 *
 * Per the ground rules (§16.0), the logic lives in the pure modules:
 *  - `Reshape.ts` builds the bullet pool (P7),
 *  - `Weave.ts` + `Weave_Live.ts` weave it into an installment (P8),
 *  - `Scoring.ts` scores the finished round,
 *  - `Round.ts` owns the two view models -- the LABEL-FREE `HuntRoundView` the Play tab renders, and
 *    the labelled `HuntRevealView` shown after submission.
 *
 * This wrapper is the only place that:
 *  1. **generates** a round end-to-end (`GenerateRound_Live`) and saves it with `status: "unjudged"`,
 *     which is P9's own new status value (§16.2 P9);
 *  2. **submits** it (`SubmitRound_Live`): persists the answers, stamps `revealedAt`, builds the
 *     labelled reveal view, and bumps the §4.5 exposure counters;
 *  3. **restores** progress after a reload (`SaveAnswers_Live` writes the same `answers` field the
 *     submit path writes, so there is no separate draft storage to keep in sync).
 *
 * ## Why `revealedAt` rather than a new round status
 *
 * `status` belongs to the JUDGE loop (§9.4: `unjudged`/`ok`/`easy`/`discarded`). A round the user has
 * played is still `unjudged` until P10 has run, so overloading `status` for "played" would make the two
 * orthogonal facts unrepresentable. `revealedAt` is an `extras` JSON field, so it needed no app-server
 * change (the same reason `publicText` needed none in P4).
 *
 * ## Exposure counters (§4.5)
 *
 * `exposureRoundCount` is bumped for every real bullet SHOWN, at submit time. `exposureRevealCount` is
 * bumped for the real bullets the reveal actually displays, because the reveal is what refreshes the
 * memory -- §4.5 weights it above an in-round exposure. Opening the FULL dream is a stronger exposure
 * still and is counted at that action (`RecordRevealOpen_Live`), which is why it is a separate call
 * rather than part of submit.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §10, §12, §13.1, §4.5, §16.2 P9.
 */
import {GetAsync} from "mobx-graphlink";
import {MeID} from "../Server/DB/users.js";
import {EchoRound, GetEchoRounds} from "../Server/DB/echoRounds/@EchoRound.js";
import {AddEchoRound, UpdateEchoRound} from "../Server/Commands/EchoRoundCommands.js";
import {UpdateEchoCorpusBullet} from "../Server/Commands/EchoCorpusBulletCommands.js";
import {EchoCorpusBullet} from "../Server/DB/echoCorpusBullets/@EchoCorpusBullet.js";
import {GetMyEchoCorpusBullets} from "./Index_Live.js";
import {GetMyEchoProfile, DEFAULT_X_SHARE} from "./Profiles_Live.js";
import {BuildBulletPool_Live, IsBulletPoolReady} from "./Reshape_Live.js";
import {GetActiveStory_Live, GetWeaveContext_Live, IsWorldPoolReady, WeavePool_Live} from "./Weave_Live.js";
import {IsCharacterPoolReady, StoryCharacterMissing} from "./Characters_Live.js";
import {
	AdvanceStorySummary_Live,
	ApplyDoorDecision_Live,
	ApplySuspicionAfterRound_Live,
	ClearDoorOverride_Live,
	EnsureActiveStory_Live,
	EnsureStoryWorld_Live,
	GetDoorState_Live,
	SuspicionApplicationResult,
} from "./Story_Live.js";
import {LLMCallCounter} from "./LLM.js";
import {Answer, EchoRoundStatus, JudgeLoopSummary, RoundSlot} from "./Types.js";
import {JudgeRound_Live} from "./Judge_Live.js";
import {
	BuildRevealView,
	HuntRevealView,
	HuntRoundView,
	IsRoundPlayable,
	IsRoundRevealed,
	PickPlayableRound,
	RevealedSource,
	SelectRevealedSlotIds,
	SelectShownRealSlotIds,
	SetTellPhraseInAnswers,
	ToHuntRoundView,
} from "./Round.js";
import {
	ApplyMarkedFlags,
	CreateNeutralAnswers,
	SelectMarkedSlotIds,
	ToAnswerList,
	ToConfidenceList,
} from "./Scoring.js";

/** Default bullets per round. Ten is enough that the top-X bucket is meaningful (a 3-bullet round makes
 * the mark list nearly deterministic) and small enough to play in a couple of minutes. */
export const DEFAULT_ROUND_BULLET_COUNT = 10;

// reads
// ==========

export function GetMyEchoRounds(): EchoRound[] {
	return GetEchoRounds(MeID() ?? null);
}

/** The round the Play tab should show (fresh, or half-played after a reload). SYNC: from an async path
 * wrap the call site in `GetAsync`, exactly like the other `*_Live` readers.
 *
 * SCOPED TO THE SELECTED STORY (P11 follow-up). With several stories running at once, picking the newest
 * unrevealed round across ALL of them would mean switching stories still left you looking at the previous
 * one's round -- or, worse, half-playing a round belonging to a story you are no longer in. Story-less
 * pre-P11 rounds (`storyId == null`) are never shown while a story is selected, for the same reason: they
 * cannot be played into the story you are in.
 *
 * With no story selected the scope is story-less rounds only, which is the pre-P11 behaviour for a user who
 * has none. */
export function GetPlayableRound_Live(): EchoRound|null {
	const userID = MeID();
	if (userID == null) return null;

	const storyId = GetActiveStory_Live()?.id ?? null;
	const rounds = GetEchoRounds(userID).filter(round=>(round.storyId ?? null) == storyId);
	return PickPlayableRound(rounds);
}

/** Every revealed round, newest first (the history/stats surface P13 builds on). Account-wide: use
 * `GetPlayableStoryRevealedRounds_Live` for anything that belongs to the round being played. */
export function GetRevealedRounds_Live(): EchoRound[] {
	return GetMyEchoRounds()
		.filter(round=>IsRoundRevealed(round))
		.sort((a, b)=>b.createdAt - a.createdAt);
}

/** The finished rounds of the story the PLAY tab is scoped to, newest first. Uses the same scope rule as
 * `GetPlayableRound_Live` -- the active story's rounds, or story-less ones when no story is active -- so the
 * list below the hunt cannot show (or re-open) a round from a story the player is no longer in.
 *
 * Deliberately not `GetStoryRevealedRounds_Live`: that one is keyed on an explicit id and returns nothing for
 * `null`, while the Play tab's "no story" case is exactly the pre-P11 story-less rounds. */
export function GetPlayableStoryRevealedRounds_Live(): EchoRound[] {
	const storyId = GetActiveStory_Live()?.id ?? null;
	return GetRevealedRounds_Live().filter(round=>(round.storyId ?? null) == storyId);
}

/** The revealed rounds of ONE story, newest first (the Story tab's installments). Exists because
 * `GetRevealedRounds_Live` is account-wide, which is right for a stats view and wrong for a story. */
export function GetStoryRevealedRounds_Live(storyId: string|n): EchoRound[] {
	if (storyId == null) return [];
	return GetMyEchoRounds()
		.filter(round=>round.storyId == storyId && IsRoundRevealed(round))
		.sort((a, b)=>b.createdAt - a.createdAt);
}

/** The label-free view the Play tab renders (§16.0). Null when there is nothing playable. */
export function GetPlayableRoundView_Live(): HuntRoundView|null {
	return ToHuntRoundView(GetPlayableRound_Live());
}

/** The corpus rows a round's real slots point at -- what a reveal needs to show each echo's raw text and
 * date (§10.4), and what the judge inspector uses to show a bullet's pre-transformation text.
 *
 * Keyed by the corpus row `id`, because that is what a slot's `sourceId` is: `Reshape_Live.RowToRealBulletPool`
 * sets `id: row.id`. This used to key by `bulletId`, so EVERY lookup missed and the reveal silently fell back
 * to showing the reshaped text as if it were the original (and the exposure counters never matched a row). */
export function GetCorpusRowsById(): Map<string, EchoCorpusBullet> {
	const rows = new Map<string, EchoCorpusBullet>();
	for (const row of GetMyEchoCorpusBullets()) rows.set(row.id, row);
	return rows;
}

/** The reveal view for an already-submitted round, from the DB (used when re-opening a finished round in
 * a later session). Null when the round has not been revealed. */
export function GetRevealView_Live(round: EchoRound|null): HuntRevealView|null {
	if (round == null || !IsRoundRevealed(round)) return null;
	const corpus = GetCorpusRowsById();
	return BuildRevealView(round, round.answers ?? [], BuildSourcesFromCorpus(round.slots ?? [], corpus));
}

/** Maps a round's real slots onto the corpus rows they came from (§10.4). A missing row still produces
 * an entry -- omitting it would read as "this bullet was a fake", the exact confusion the reveal exists
 * to remove. */
function BuildSourcesFromCorpus(slots: RoundSlot[], corpus: Map<string, EchoCorpusBullet>): RevealedSource[] {
	const sources: RevealedSource[] = [];
	for (const slot of slots) {
		if (slot.sourceKind != "real") continue;
		const row = corpus.get(slot.sourceId);
		sources.push({
			slotId: slot.slotId,
			rawText: row?.rawText ?? slot.reshapedText,
			date: row?.date ?? null,
			heading: row?.heading ?? null,
			dreamId: row?.dreamId ?? null,
		});
	}
	return sources;
}

// generation (§16.2 P9: P7 -> P8 -> save with status "unjudged")
// ==========

export type GenerateRoundResult =
	| {ok: true, round: EchoRound, slotCount: number, outputTokens: number, judgeSummary: JudgeLoopSummary|null}
	| {ok: false, error: string};

/** Why `GetWeaveContext_Live` returned null, in words that point at the tab that fixes it.
 *
 * The context has three distinct empty states (no world, no character, a story whose character is gone)
 * and one shared null return. Reporting all three as one message would leave the user hunting for the
 * right tab, and "no character" is by far the most likely now that the pool starts empty on purpose
 * (§8.7). SYNC, so it must be called from a path that already wrapped `GetAsync`. */
function GetWeaveContextFailureReason_Live(): string {
	const story = GetActiveStory_Live();
	if (story != null && StoryCharacterMissing(story)) {
		return "This story's character is no longer in your pool (it was deleted), so the story cannot continue."
			+ " Pick a character for it -- until then no round can be built.";
	}
	if (!IsCharacterPoolReady()) {
		return "No characters in your pool -- add one on the Characters tab (the autogen buttons can draft it).";
	}
	if (!IsWorldPoolReady()) {
		return "No worlds in your pool -- add one on the Worlds tab.";
	}
	return "The story context could not be assembled -- check the Worlds and Characters tabs.";
}

/** Builds one complete round and saves it (§16.2 P9).
 *
 * Order matters: the bullet pool is BUILT first (P7), then woven (P8), and only a VALIDATED weave is
 * saved. A round whose weave failed is not persisted at all -- a stored round with no woven text would
 * show up in the Play tab as unplayable and would have to be cleaned up by hand, whereas an error
 * message costs one button press.
 *
 * `X` comes from the profile (falling back to §16.1's default) so the round's difficulty setting is
 * snapshotted onto the row, which is what makes old rounds interpretable after a settings change (§13.1).
 */
export async function GenerateRound_Live(options?: {
	bulletCount?: number;
	xShare?: number;
	seedingEnabled?: boolean;
	/** P10 §5.5: overrides the profile's rejection-sampling toggle for this round. Omit to use the profile. */
	rejectionSampling?: boolean|n;
	/** injected in tests; production uses `Math.random` */
	rng?: () => number;
	/** when set, the round is attached to this story instead of the active one (P11) */
	storyId?: string|n;
}): Promise<GenerateRoundResult> {
	const userID = MeID();
	if (userID == null) return {ok: false, error: "You must be signed in to play a round."};
	if (!IsBulletPoolReady()) {
		return {ok: false, error: "The corpus or the word list is not ready -- index the corpus and let the word list load first."};
	}

	const profile = await GetAsync(()=>GetMyEchoProfile());
	const xShare = Math.max(0, Math.min(1, options?.xShare ?? profile?.xShare ?? DEFAULT_X_SHARE));
	const bulletCount = Math.max(2, Math.round(options?.bulletCount ?? DEFAULT_ROUND_BULLET_COUNT));
	const rng = options?.rng ?? Math.random;
	const counter = new LLMCallCounter();

	// 0) the story (§8.3, P11). Every round belongs to one now: `summary`, `worldId`, `suspicion` and the
	//    world history all live there. `EnsureActiveStory_Live` starts one if there is none and fails with
	//    an actionable message if the pools are empty, which is strictly better than building a round that
	//    cannot continue afterwards. An explicit `storyId` bypasses it (the dev tools).
	let storyId = options?.storyId ?? null;
	if (storyId == null) {
		const started = await EnsureActiveStory_Live({rng});
		if (!started.ok) return {ok: false, error: started.error};
		storyId = started.story.id;
	}

	// 0b) §11.5 W5: one live door at a time. Refusing is the whole point -- the old code silently overwrote
	//     the story's pending flag and left the round still holding an offer, which is how two doors ended up
	//     in play for one story. The player is one click away, on the Play tab.
	const doorInPlay = await GetAsync(()=>GetDoorState_Live(GetActiveStory_Live()));
	if (doorInPlay != null) {
		return {ok: false, error: doorInPlay.awaitingCharacter
			? "There is already a door waiting in the story -- a round that presents it has to be played first."
			: "Answer the door on your last round (Play tab) before building another, or the story would move twice."};
	}

	// 0c) §11.5 W6: if the story's world was deleted from the pool, move it into a real one NOW and record
	//     the move. Without this the context silently drew a different world per round while the history
	//     recorded nothing.
	const recovery = await EnsureStoryWorld_Live(storyId, {rng});
	if (recovery.moved && recovery.toWorld == null) {
		return {ok: false, error: "The world this story was in is gone and there is nothing in your pool to replace it. Add a world on the Worlds tab."};
	}

	// 1) the bullet pool (P7)
	const pool = await BuildBulletPool_Live({
		N: bulletCount,
		X: xShare,
		rng,
		seedingEnabled: options?.seedingEnabled,
		rejectionSampling: options?.rejectionSampling,
		counter,
	});
	if (pool == null) return {ok: false, error: "The corpus or the word list is not ready."};
	if (pool.slots.length == 0) return {ok: false, error: "Every bullet was dropped, so the round would be empty."};

	// 2) the weave context (P8/P10.5). Wrapped in `GetAsync` because this runs from a click handler, and
	//    the context reads `echoStories`, a collection no tab renders (so it is never subscribed to and a
	//    cold read there would throw a bail nothing catches).
	//
	//    §11.5 W2/W3/W4: the context already carries the staged door (the CHARACTER's decision), the door
	//    TARGET (so the weaver can describe the far side honestly), and the arrival block after a move. All
	//    three are derived inside `GetWeaveContext_Live` from the rounds and the visit history.
	const context = await GetAsync(()=>GetWeaveContext_Live({rng}));
	if (context == null) return {ok: false, error: await GetWeaveContextFailureReason_Live()};
	const doorRequested = context.requestDoor == true;

	// 3) the weave (P8). A failed weave FAILS the round (§16.2 P8: never degrade to a partial weave).
	const woven = await WeavePool_Live(pool.slots, {context, counter});
	if (woven.slots == null) {
		return {ok: false, error: "The weaver could not produce a valid installment, so the round was not saved. Try again."};
	}

	// §11.5 W3: the offer's target is fixed HERE, from the context, because that is the world the prompt was
	// told the door leads to. Storing it makes resolution a lookup instead of a fresh draw that could
	// contradict the prose it already wrote.
	const rawOffer = woven.result.output?.doorOffer ?? null;
	const doorOffer = rawOffer != null
		? {...rawOffer, targetWorldId: context.doorTarget?.id ?? null}
		: null;

	// 4) persist. `ApplyWeave` already reordered the slots into STORY order, which is the order the
	//    player reads and the order every later phase assumes.
	const round = new EchoRound({
		storyId,
		status: "unjudged" as EchoRoundStatus,
		settings: {
			bulletCount,
			seedingEnabled: options?.seedingEnabled ?? true,
			// snapshotted so an old round stays interpretable after the settings change (§11, §13.1)
			xShare,
			yTarget: profile?.yTarget ?? null,
			rewriteStrength: profile?.difficulty?.rewriteStrength ?? null,
			// P11: recorded so the round log shows whether a door was ASKED for or merely offered
			doorRequested,
		},
		xShare,
		yTarget: profile?.yTarget ?? null,
		seedingEnabled: options?.seedingEnabled ?? true,
		worldId: context.world.id,
		characterId: context.character.id,
		frame: context.frame ?? null,
		storylineSummaryIn: context.summary ?? null,
		storylineSummaryOut: woven.result.output?.summaryOut ?? null,
		// §11.5 W2: when a door was STAGED, the round's own record of it is the source round's offer; the
		// character's decision comes back from the weave and is applied below.
		doorOffer: doorOffer,
		doorDecision: woven.result.output?.doorDecision ?? null,
		slots: woven.slots,
		answers: [],
		generatedBullets: pool.generatedBullets,
		judgeLog: [],
		outputTokensTotal: counter.outputTokens,
		llmCallsTotal: counter.callCount,
	});

	const id = (await new AddEchoRound({entry: round}).RunOnServer()).id;

	// 5) the story's continuity (§8.1, §8.3): advance the summary, apply the CHARACTER's door decision (the
	//    only thing that can move the story), and CONSUME the one-shot override request -- otherwise every
	//    later round would insist on a door and the story could never settle anywhere.
	await AdvanceStorySummary_Live(storyId, woven.result.output?.summaryOut ?? null);

	const stagedState = context.stagedDoor != null ? await GetAsync(()=>GetDoorState_Live(GetActiveStory_Live())) : null;
	if (stagedState != null && round.doorDecision != null) {
		await ApplyDoorDecision_Live({
			storyId, roundId: stagedState.roundId, decision: round.doorDecision,
			// the offer lives on the SOURCE round -- the one the player accepted -- and carries the target
			offer: stagedState.offer,
			rng,
		}).catch(ex=>console.warn(`Echo Hunt: could not apply the door decision (${ex}).`));
	}

	if (doorRequested) await ClearDoorOverride_Live(storyId);

	// 6) the judge loop (§3: "weave -> JUDGE LOOP -> HUNT"). A round the player is about to see must
	//    already be stranger-proof, or their score measures the generator instead of their familiarity
	//    (§2 invariant #1). The loop runs AFTER the save so a judge-side failure cannot lose the round:
	//    it stays `unjudged` and the Play tab offers to judge it again.
	const savedRound = new EchoRound({...round, id});
	const judged = await JudgeRound_Live(savedRound, {counter});
	if (!judged.ok) {
		// a judged-round failure is NOT a round failure: the round is saved and playable, just unjudged
		console.warn(`Echo Hunt: the round was saved but could not be judged (${judged.error}).`);
	}

	return {
		ok: true,
		round: judged.ok ? judged.round : savedRound,
		slotCount: woven.slots.length,
		outputTokens: counter.outputTokens,
		judgeSummary: judged.ok ? judged.result.summary : null,
	};
}

/** Runs the judge loop over an EXISTING round (§9, §16.2 P10). Exists so rounds generated before P10 --
 * which are all stuck as `unjudged` -- can be judged without being regenerated, and so a round whose
 * background judging failed can be retried.
 *
 * SYNC-callable from a React handler: the whole body goes through `GetAsync`, because `echoRounds` may not
 * be subscribed to at the moment the button is pressed and a cold read would throw an uncaught bail. */
export async function JudgeExistingRound_Live(roundID: string): Promise<JudgeExistingRoundResult> {
	const round = await GetAsync(()=>GetMyEchoRounds().find(a=>a.id == roundID) ?? null);
	if (round == null) return {ok: false, error: "That round no longer exists."};

	const judged = await JudgeRound_Live(round);
	if (!judged.ok) return judged;
	return {ok: true, summary: judged.result.summary, status: judged.result.status};
}

export type JudgeExistingRoundResult =
	| {ok: true, summary: JudgeLoopSummary, status: string}
	| {ok: false, error: string};

// saving progress (§16.2 P9 "reloading mid-round restores progress")
// ==========

/** The round's stored answers, for restoring an in-progress sheet after a reload (§16.2 P9). Empty for
 * a round that has never been touched, so the Play tab can seed neutral confidences from it directly. */
export function GetStoredAnswersForRound_Live(roundID: string): Answer[] {
	return GetMyEchoRounds().find(a=>a.id == roundID)?.answers ?? [];
}

/** Persists the answers collected so far, without revealing anything.
 *
 * Writing on every change would be one command per slider tick, so callers pass the whole sheet when it
 * changes meaningfully (the Play tab debounces it). `slotOrder` is passed in rather than read from the DB
 * because the caller is holding it already, and re-reading the round here would add a second bail-prone
 * accessor to a path that runs on a timer.
 */
export async function SaveAnswers_Live(roundID: string, drafts: Parameters<typeof ToAnswerList>[1], slotOrder?: string[]|n): Promise<void> {
	const answers = ToAnswerList(slotOrder, drafts);
	// the real share is needed to derive the guess-list; it comes from the stored round, since the
	// caller deliberately only knows the label-free view (§16.0)
	const markedSlotIds = SelectMarkedSlotIds(ToConfidenceList(answers), CountRealSlots(roundID), slotOrder ?? undefined);
	await new UpdateEchoRound({id: roundID, updates: {answers: ApplyMarkedFlags(answers, markedSlotIds)}}).RunOnServer();
}

/** How many of a round's slots are real. Read from the DB by id, so the caller does not have to carry
 * the labelled slot list around. Returns 0 when the round is not loaded. */
function CountRealSlots(roundID: string): number {
	const round = GetMyEchoRounds().find(a=>a.id == roundID) ?? null;
	return (round?.slots ?? []).filter(slot=>slot.sourceKind == "real").length;
}

// submitting (§10.1, §12, §13.1)
// ==========

export type SubmitRoundResult =
	| {ok: true, reveal: HuntRevealView, highlightedSlotIds: string[], suspicion: SuspicionApplicationResult|null}
	| {ok: false, error: string};

/** Submits a round: scores it, stamps `revealedAt`, persists the answers, and returns the labelled
 * reveal view (§10.1 + §16.2 P9).
 *
 * `highlightedSlotIds` is returned alongside the reveal so the Play tab can freeze exactly the guess-list
 * the user was looking at -- the reveal's per-slot marks must not be re-derived from a different tiebreak
 * (§10.1: the X highest-rated entries "are highlighted to show their inclusion in your guess list").
 *
 * Exposure counters are updated here rather than at generation: a round that is generated but never
 * played did not show the user anything, and counting it would decay the fallowness weight of bullets
 * nobody saw.
 *
 * P11 (§8.4): the SUSPICION METER moves here for the same reason, and from the SCORE -- so it depends only
 * on rounds that were actually played. `suspicion` in the result is null for a round with no story (a
 * pre-P11 row), and it reports `ended: false` whenever applying suspicion is switched off in the profile.
 */
export async function SubmitRound_Live(roundID: string, drafts?: Parameters<typeof ToAnswerList>[1]): Promise<SubmitRoundResult> {
	const round = await GetAsync(()=>(GetMyEchoRounds().find(a=>a.id == roundID) ?? null));
	if (round == null) return {ok: false, error: "That round no longer exists."};
	if (!IsRoundPlayable(round)) return {ok: false, error: "That round has no woven text, so it cannot be played."};
	if (IsRoundRevealed(round)) {
		// re-submitting would overwrite a finished answer sheet with a fresh one, silently changing the
		// score of a round the user has already seen the answer to
		return {ok: false, error: "That round has already been revealed."};
	}

	const slots = round.slots ?? [];
	const slotOrder = slots.map(slot=>slot.slotId);
	const answers = drafts != null
		? ToAnswerList(slotOrder, drafts)
		: (round.answers ?? []).length > 0
			? round.answers!
			: ToAnswerList(slotOrder, CreateNeutralAnswers(slots));

	const realCount = slots.filter(slot=>slot.sourceKind == "real").length;
	const highlightedSlotIds = SelectMarkedSlotIds(ToConfidenceList(answers), realCount, slotOrder);
	const finalAnswers = ApplyMarkedFlags(answers, highlightedSlotIds);

	const reveal = BuildRevealView(round, finalAnswers, BuildSourcesFromCorpus(slots, GetCorpusRowsById()), {markedSlotIds: highlightedSlotIds});
	if (reveal == null) return {ok: false, error: "That round could not be scored."};

	await new UpdateEchoRound({id: roundID, updates: {
		answers: finalAnswers,
		revealedAt: Date.now(),
	}}).RunOnServer();

	await BumpExposureCounters_Live(slots, reveal);

	// P11 (§8.4): advance the meter from this round's score. Deliberately AFTER the reveal is built -- the
	// reveal is the user's record of the round, and a failure in the interlude must not cost them the score.
	const suspicion = await ApplySuspicionAfterRound_Live({
		storyId: round.storyId ?? "",
		hits: reveal.score.hits,
		misses: reveal.score.misses,
	}).catch(ex=>{
		// an interlude failure is a narration problem, not a scoring one; the round stands either way
		console.warn(`Echo Hunt: the suspicion meter could not be advanced (${ex}).`);
		return null;
	});

	return {ok: true, reveal, highlightedSlotIds, suspicion};
}

/** Bumps the §4.5 exposure counters for the round's real bullets: `exposureRoundCount` for every real
 * bullet shown, `exposureRevealCount` for the ones the reveal displayed.
 *
 * Both increments are read-modify-write per row, which is fine here: only this user writes these rows,
 * and the values feed a sampling WEIGHT rather than a score -- a lost increment from two tabs open at
 * once would change how often a bullet is drawn, not whether a round is valid.
 */
async function BumpExposureCounters_Live(slots: RoundSlot[], reveal: HuntRevealView|null): Promise<void> {
	const corpus = GetCorpusRowsById();
	const shown = new Set(SelectShownRealSlotIds({id: "", slots}));
	const revealed = new Set(SelectRevealedSlotIds(reveal));

	for (const slot of slots) {
		if (slot.sourceKind != "real" || !shown.has(slot.slotId)) continue;
		const row = corpus.get(slot.sourceId);
		if (row == null) continue;

		const updates: Partial<EchoCorpusBullet> = {
			exposureRoundCount: (row.exposureRoundCount ?? 0) + 1,
			lastUsed: Date.now(),
		};
		if (revealed.has(slot.slotId)) {
			updates.exposureRevealCount = (row.exposureRevealCount ?? 0) + 1;
		}
		await new UpdateEchoCorpusBullet({id: row.id, updates}).RunOnServer();
	}
}

/** Records the STRONG exposure of opening a real bullet's full source dream (§10.4). Deliberately a
 * separate call from submit: §10.4 wants the full dream opened only on request, and logging that as a
 * stronger exposure is the whole point of keeping it apart. */
export async function RecordRevealOpen_Live(slotSourceId: string): Promise<void> {
	const row = GetCorpusRowsById().get(slotSourceId);
	if (row == null) return;
	await new UpdateEchoCorpusBullet({id: row.id, updates: {
		exposureRevealCount: (row.exposureRevealCount ?? 0) + 1,
		lastUsed: Date.now(),
	}}).RunOnServer();
}

/** The tell phrase the user supplied for a fake they spotted (§10.3). P13 promotes these into the
 * cliché blacklist with the user's confirmation, so this only persists the phrase on the answer -- it
 * never writes the blacklist itself.
 *
 * An empty phrase DELETES the stored note (and its `llmTellFlag`), which is what the reveal's Save button
 * does when the box has been cleared: a blank box that still carried a stored phrase could not be
 * distinguished from a failed clear. */
export async function SaveTellPhrase_Live(roundID: string, slotId: string, phrase: string|null): Promise<void> {
	const round = GetMyEchoRounds().find(a=>a.id == roundID) ?? null;
	if (round == null) return;

	await new UpdateEchoRound({id: roundID, updates: {answers: SetTellPhraseInAnswers(round.answers, slotId, phrase)}}).RunOnServer();
}

/** Marks a round DISCARDED, so it stops being offered on the Play tab without being deleted (§11.5 W7).
 *
 * Used by the stale-round notice: a round written in a world the story has since left is still internally
 * coherent, so throwing the row away would destroy something the player paid for. `status` is the judge
 * loop's field, and `discarded` is already one of its values (§9.4), so this reuses it rather than adding a
 * second way to say "do not play this" -- `IsRoundRevealed` still governs the playable set, and this ALSO
 * stamps `revealedAt` so the round leaves it. The judge summary is left alone: it describes a round that
 * really was written. */
export async function MarkRoundDiscarded_Live(roundID: string): Promise<void> {
	const round = GetMyEchoRounds().find(a=>a.id == roundID) ?? null;
	if (round == null) return;
	if (round.revealedAt != null) return; // already out of the playable set; nothing to do

	await new UpdateEchoRound({id: roundID, updates: {
		status: "discarded" as EchoRoundStatus,
		revealedAt: Date.now(),
	}}).RunOnServer();
}

// submit-time helpers live in `Round.ts` / `Scoring.ts`; this wrapper only owns DB traffic
